<template>
  <div>
    <div class="card card border-primary mb-3">
      <div class="card-header d-flex justify-content-between align-items-center">
        {{ title | capitalize }}
        <i class="icon-about icon-sm" @click="showInfoModal=true" />
      </div>

      <div class="card-body">
        <dropzone v-if="!isProcessingFile"
                  id="update-dropzone"
                  :url="url"
                  :start-upload.sync="startUpload"
                  icon="icon-file-csv"
                  accepted-files=".csv"
                  @dropzone-file-added="startUpload=true"
                  @dropzone-file-removed="startUpload=false"
                  @dropzone-success="onUploadSuccess"
                  @dropzone-error="onUploadError"
        />

        <div v-if="isProcessingFile" class="drop-container">
          <div>
            <div v-if="processingStatus==='IN_PROGRESS'" class="d-flex justify-content-between">
              {{ $t('dropzoneAsyncBulk:processingFile') | capitalize }}
            </div>
            <div v-else-if="processingStatus==='OK' && processingPercentage===0" class="d-flex justify-content-between">
              {{ $t('dropzoneAsyncBulk:emptyFile') | capitalize }}
            </div>
            <div v-else-if="processingStatus==='OK'" class="d-flex justify-content-between">
              {{ $t('dropzoneAsyncBulk:allCreated', {equipment: equipment}) | capitalize }}
              <span class="icon-check no-pointer text-success" />
            </div>
            <div v-else-if="processingStatus==='KO'" class="d-flex justify-content-between">
              {{ $t('dropzoneAsyncBulk:someErrors', {equipment: equipment}) | capitalize }}
              <span class="icon-remove no-pointer text-danger" />
            </div>

            <div class="dz-preview">
              <div class="dz-progress"><span :style="{width:processingPercentage+'%'}" class="dz-upload" /></div>

              <div v-if="processingError && processingError.length>0" class="dropzone-error-container font-italic">
                <ul>
                  <li v-for="(error, indexError) in processingError" :key="indexError">
                    {{ error.main }}
                    <ul v-if="error.details && error.details.length>0" class="ml-2">
                      <li v-for="(detail, indexDetail) in error.details" :key="indexDetail" :class="[error.type==='warning' ? 'text-warning' : 'text-danger']">{{ detail }}</li>
                    </ul>
                  </li>
                </ul>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <a v-if="processingStatus!=='IN_PROGRESS' && errorFileUrl" id="downloadErrorFileButton" :href="errorFileUrl" :title="$t('dropzoneAsyncBulk:downloadErrorFileTitle') | capitalize" class="dz-remove">
                    {{ $t('dropzoneAsyncBulk:downloadErrorFile') | capitalize }}
                  </a>
                </div>

                <div :class="(processingStatus==='KO' && errorFileUrl) ? 'col-md-6' : 'col-md-12'">
                  <a v-if="processingStatus!=='IN_PROGRESS'" id="uploadAnotherFileButton" class="dz-remove" @click.prevent="reset">
                    {{ $t('dropzoneAsyncBulk:uploadAnotherFile') | capitalize }}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card-footer">
        <button id="downloadTemplateButton" class="btn btn-primary" @click="downloadTemplate">{{ $t('dropzoneAsyncBulk:template') | capitalize }}</button>
      </div>
    </div>

    <modal id="infoModal" v-model="showInfoModal" :title="$t('dropzoneAsyncBulk:templateTitle') | capitalize" size="lg">
      <div slot="modal-body" class="form-horizontal">
        <p>{{ $t('dropzoneAsyncBulk:templateDescription') }}</p>

        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th>{{ $t('dropzoneAsyncBulk:property') | capitalize }}</th>
              <th>{{ $t('dropzoneAsyncBulk:format') | capitalize }}</th>
              <th>{{ $t('dropzoneAsyncBulk:value') | capitalize }}</th>
              <th>{{ $t('dropzoneAsyncBulk:required') | capitalize }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="(prop, header) in configuration" :key="header">
              <td>{{ header }}</td>
              <td>
                <template v-if="prop.format">{{ $t(prop.format) | capitalize }}</template>
              </td>
              <td>
                <div v-if="prop.length">{{ $t('dropzoneAsyncBulk:length', {value: prop.length}) | capitalize }}</div>
                <div v-if="prop.maxLength">{{ $t('dropzoneAsyncBulk:maxLength', {value: prop.maxLength}) | capitalize }}</div>
                <div v-if="prop.min !== undefined">{{ $t('dropzoneAsyncBulk:minValue', {value: prop.min}) | capitalize }}</div>
                <div v-if="prop.max !== undefined">{{ $t('dropzoneAsyncBulk:maxValue', {value: prop.max}) | capitalize }}</div>
                <div v-if="prop.values">
                  {{ $t('dropzoneAsyncBulk:allowedValue') | capitalize }}
                  <ul class="mb-0"><li v-for="(value, indexVal) in prop.values" :key="indexVal">{{ value }}</li></ul>
                </div>
              </td>
              <td class="text-center">{{ prop.required ? '*' : '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </modal>
  </div>
</template>

<script>
import { isEmpty } from 'lodash-es'
import saveAs from 'file-saver'
// TODO remove clientHTTP dependency to be able to extract to an external repo
import clientHTTP from '@/modules/clientHTTP'
import constants from '@/configuration/constants'
import eventBus from '@/modules/eventBus'

export default {
  props: {
    // Title
    title: {
      type: String,
      required: true
    },
    // Url to request with the file
    url: {
      type: String,
      required: true
    },
    // Configuration file with headers and fields type
    configuration: {
      type: Object,
      required: true
    },
    // Equiment to create
    equipment: {
      type: String,
      required: true
    },
    // Template name with no extension (will be .csv)
    templateName: {
      type: String,
      default: 'template'
    }
  },

  data () {
    return {
      // Info modal
      showInfoModal: false,
      // Dropzone
      startUpload: false,
      // Display processing view
      isProcessingFile: false,
      proccessingUrl: '',
      processingStatus: 'IN_PROGRESS',
      processingPercentage: 0,
      processingError: '',
      processingTimeout: null,
      errorFileUrl: ''
    }
  },

  methods: {
    reset () {
      // Dropzone
      this.startUpload = false
      // processing view
      this.isProcessingFile = false
      this.proccessingUrl = ''
      this.processingStatus = 'IN_PROGRESS'
      this.processingPercentage = 0
      this.processingError = ''
      this.processingTimeout = null
      this.errorFileUrl = ''
    },

    // Upload file is a success
    onUploadSuccess (file, response) {
      this.proccessingUrl = file.xhr.getResponseHeader('Content-Location') || file.xhr.getResponseHeader('Location')

      // If not Content-Location header to poll to get status, display a success message
      if (!this.proccessingUrl) {
        this.processingStatus = 'OK'
        eventBus.$emit('async-bulk:success')
        return
      }

      // Switch to process view
      this.isProcessingFile = true
      // And start polling to get status (display the processing text during 1s...)
      this.processingTimeout = setTimeout(this.getUploadStatus, 1000)
    },

    getUploadStatus () {
      clientHTTP.get(constants.gmsUrl + this.proccessingUrl)
        .then((response) => {
          const responseData = response.data

          // Clear polling timeout
          clearTimeout(this.processingTimeout)

          // Just don't divide by 0 when file is empty...
          if (responseData.total) {
            // Calculate percentage done
            this.processingPercentage = responseData.done / responseData.total * 100
          }

          // Get status
          this.processingStatus = responseData.status

          // Set error if KO
          if (!isEmpty(responseData.error)) {
            this.processingError = this.$utils.processErrors(responseData.error)

            // Get error file url
            if (responseData.links) {
              this.errorFileUrl = this.$utils.getLinkHref(responseData, 'csvErrors')
            }
            eventBus.$emit('async-bulk:error')
          }

          // Start polling if IN_PROGRESS
          if (this.processingStatus === 'IN_PROGRESS') {
            if (this._isDestroyed || this._isBeingDestroyed) return
            this.processingTimeout = setTimeout(this.getUploadStatus, 2000)
          }

          // Emit success event if OK
          if (this.processingStatus === 'OK') {
            eventBus.$emit('async-bulk:success')
          }
        })
        .catch((e) => {
          // Fails to get status, just retry in 10 seconds
          clearTimeout(this.processingTimeout)
          if (this._isDestroyed || this._isBeingDestroyed) return
          this.processingTimeout = setTimeout(this.getUploadStatus, 10000)
        })
    },

    // Error when uploading file
    onUploadError (file, error) {
      // Switch to process view
      this.isProcessingFile = true

      this.processingStatus = 'KO'
      this.processingError = this.$utils.processErrors(error)
      eventBus.$emit('async-bulk:error', { error: error })
    },

    // Dwonload CSV template
    downloadTemplate () {
      const properties = { type: 'csv/text' }
      let file = null

      // Get headers from config file
      const headers = Object.keys(this.configuration)
      const data = headers.map((h, i) => { return i === headers.length - 1 ? h : h + ',' })

      try {
        file = new File(data, this.templateName + '.csv', properties)
      } catch (e) {
        file = new Blob(data, properties)
      }
      saveAs(file, this.templateName + '.csv')
    }
  }
}
</script>
